/**
 * Protected Route Component for TECDocChart Healthcare Application
 * 
 * Provides authentication and authorization checking for React routes
 * Integrates with Issue #67 hybrid authentication system
 * Enforces healthcare employee context requirements
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from '../shared/Loading';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requireEmployee?: boolean;
}

interface AuthState {
  authenticated: boolean;
  user: any;
  employee: any;
  permissions: string[];
  loading: boolean;
  error: string | null;
}

function ProtectedRoute({ 
  children, 
  requiredPermissions = [], 
  requireEmployee = true 
}: ProtectedRouteProps) {
  const [authState, setAuthState] = useState<AuthState>({
    authenticated: false,
    user: null,
    employee: null,
    permissions: [],
    loading: true,
    error: null
  });
  
  const location = useLocation();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const hasRequiredPermission = (user: any, employee: any, permissions: string[], required: string[]) => {
    if (required.length === 0) {
      return true;
    }

    if (user?.is_superuser) {
      return true;
    }

    const effectivePermissions = employee?.permissions || permissions || [];
    return required.some((permission) => effectivePermissions.includes(permission));
  };

  const checkAuthStatus = async () => {
    try {
      // First, check if we have authentication context from Django template
      const global = (window as any);
      const authContext = global.authContext;
      
      if (authContext) {
        // Use context from Django template
        setAuthState({
          authenticated: authContext.authenticated,
          user: authContext.user,
          employee: authContext.employee,
          permissions: authContext.permissions || [],
          loading: false,
          error: null
        });
        
        // Validate authentication
        if (!authContext.authenticated) {
          redirectToLogin();
          return;
        }
        
        // Check employee requirement
        if (requireEmployee && !authContext.employee) {
          setAuthState(prev => ({
            ...prev,
            loading: false,
            error: 'Employee record required'
          }));
          return;
        }
        
        // Check permissions
        if (requiredPermissions.length > 0) {
          const hasPermission = hasRequiredPermission(
            authContext.user,
            authContext.employee,
            authContext.permissions || [],
            requiredPermissions
          );
          
          if (!hasPermission) {
            setAuthState(prev => ({
              ...prev,
              loading: false,
              error: `Missing required permissions: ${requiredPermissions.join(', ')}`
            }));
            return;
          }
        }
        
        return;
      }
      
      // Fallback: Check authentication via API
      await checkAuthViaAPI();
      
    } catch (error) {
      console.error('Authentication check failed:', error);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Authentication verification failed'
      }));
    }
  };

  const checkAuthViaAPI = async () => {
    try {
      // Try JWT token first
      const accessToken = localStorage.getItem('access_token');
      let response;
      
      if (accessToken) {
        response = await fetch('/api/auth/status/', {
          headers: { 
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          updateAuthStateFromAPI(data);
          return;
        }
      }
      
      // Fallback to session-based auth
      response = await fetch('/api/auth/status/', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        updateAuthStateFromAPI(data);
      } else {
        // Not authenticated
        redirectToLogin();
      }
      
    } catch (error) {
      console.error('API authentication check failed:', error);
      redirectToLogin();
    }
  };

  const updateAuthStateFromAPI = (data: any) => {
    setAuthState({
      authenticated: data.authenticated || false,
      user: data.user || null,
      employee: data.employee || null,
      permissions: data.permissions || [],
      loading: false,
      error: null
    });
    
    // Validate requirements
    if (!data.authenticated) {
      redirectToLogin();
      return;
    }
    
    if (requireEmployee && !data.employee) {
      setAuthState(prev => ({
        ...prev,
        error: 'Employee record required for healthcare access'
      }));
      return;
    }
    
    if (requiredPermissions.length > 0) {
      const hasPermission = hasRequiredPermission(
        data.user,
        data.employee,
        data.permissions || [],
        requiredPermissions
      );
      
      if (!hasPermission) {
        setAuthState(prev => ({
          ...prev,
          error: `Insufficient permissions: requires ${requiredPermissions.join(', ')}`
        }));
        return;
      }
    }
  };

  const redirectToLogin = () => {
    // Redirect to React login page with return path (including hash for deep links)
    const returnPath = encodeURIComponent(location.pathname + location.search + location.hash);
    window.location.href = `/login?next=${returnPath}`;
  };

  // Show loading state
  if (authState.loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Loading message="Verifying healthcare credentials..." />
      </div>
    );
  }

  // Show error state
  if (authState.error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        margin: '20px'
      }}>
        <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>
          Access Denied
        </h2>
        <p style={{ marginBottom: '20px', fontSize: '16px' }}>
          {authState.error}
        </p>
        
        {authState.error.includes('Employee record') && (
          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            padding: '15px',
            margin: '20px 0',
            textAlign: 'left'
          }}>
            <h4 style={{ color: '#856404', marginBottom: '10px' }}>
              Healthcare Employee Access Required
            </h4>
            <p style={{ color: '#856404', margin: 0 }}>
              Your account requires an associated employee record to access 
              this healthcare application. Please contact your administrator 
              to set up your employee profile.
            </p>
          </div>
        )}
        
        {authState.error.includes('permissions') && (
          <div style={{ 
            background: '#f8d7da', 
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            padding: '15px',
            margin: '20px 0',
            textAlign: 'left'
          }}>
            <h4 style={{ color: '#721c24', marginBottom: '10px' }}>
              Insufficient Permissions
            </h4>
            <p style={{ color: '#721c24', margin: 0 }}>
              You don't have the required permissions to access this page. 
              Contact your supervisor if you believe this is an error.
            </p>
          </div>
        )}
        
        <div style={{ marginTop: '30px' }}>
          <button 
            onClick={() => window.history.back()}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              marginRight: '10px',
              cursor: 'pointer'
            }}
          >
            Go Back
          </button>
          <button 
            onClick={() => window.location.href = '/dashboard'}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show protected content if authenticated and authorized
  return <>{children}</>;
}

export default ProtectedRoute;
